import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { rotateTokenCredential } from '@/api/endpoints';
import { useScmConnectionMutations } from '@/api/hooks';
import type { ScmConnection } from '@/api/schemas';
import { FormField, fieldA11y } from '@/components/FormField';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { DialogBody, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useSubmitState } from '@/features/integrations/useSubmitState';
import { BITBUCKET_TOKEN_HELP, scmReplaceError } from './scmConnectionModel';

/** State and submit of the "Replace token" dialog: rotate the credential in place, then test the connection with it. */
function useReplaceScmToken(connection: ScmConnection, onDone: () => void) {
  const { test } = useScmConnectionMutations();
  const [token, setToken] = useState('');
  const sub = useSubmitState();
  const tokenError = scmReplaceError(token);

  const run = async () => {
    await rotateTokenCredential(connection.credentialId, token.trim());
    setToken('');
    toast.success(`${connection.name}: token replaced`, { description: 'Testing the connection with it…' });
    await test.mutateAsync({ id: connection.id }).catch(() => undefined);
    onDone();
  };
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    sub.setSubmitted(true);
    sub.setProblem(null);
    if (tokenError) return;
    sub.setBusy(true);
    try {
      await run();
    } catch (err) {
      sub.setProblem(err);
    } finally {
      sub.setBusy(false);
    }
  };
  return { token, setToken, error: sub.submitted ? tokenError : undefined, onSubmit, problem: sub.problem, busy: sub.busy };
}

function TokenField({ f }: { f: ReturnType<typeof useReplaceScmToken> }) {
  return (
    <FormField id="replace-scm-token-value" label="New access token" required hint={BITBUCKET_TOKEN_HELP} error={f.error}>
      <Input data-testid="replace-scm-token-value" type="password" value={f.token} onChange={(e) => f.setToken(e.target.value)} autoComplete="off" spellCheck={false} className="font-mono sm:text-[13px]" {...fieldA11y('replace-scm-token-value', f.error, 'hint')} />
    </FormField>
  );
}

/** The dialog's content: the new token of one workspace connection. */
export function ReplaceForm({ connection, onDone }: { connection: ScmConnection; onDone: () => void }) {
  const f = useReplaceScmToken(connection, onDone);
  return (
    <form data-testid="replace-scm-token-form" onSubmit={(e) => void f.onSubmit(e)} noValidate className="flex min-h-0 flex-col" aria-label={`Replace the token of ${connection.name}`}>
      <DialogHeader>
        <DialogTitle>Replace token · {connection.name}</DialogTitle>
        <DialogDescription id="replace-scm-token-desc">The new token replaces the stored one in place; Hangar uses it from the next request and tests it now. It is stored encrypted and never shown again.</DialogDescription>
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        <TokenField f={f} />
        <ProblemAlert error={f.problem} testId="replace-scm-token-problem" />
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" data-testid="replace-scm-token-submit" loading={f.busy}>
          Replace token
        </Button>
      </DialogFooter>
    </form>
  );
}
